import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { getAuth } from 'firebase/auth';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { API_URL } from '../../constants/string-constants';
import ReportDetailsModal from './ReportDetailsModal';
import ModBanModal from './ModBanModal';
dayjs.extend(relativeTime);
import { toast } from 'react-toastify';

const FullScreenOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: var(--clr-bg);
  z-index: 2000;
  display: flex;
  flex-direction: column;
`;

const Header = styled.div`
  background-color: var(--clr-prim);
  padding: 1rem 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const Title = styled.h1`
  color: var(--clr-sec);
  margin: 0;
  font-size: 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const RoomBadge = styled.span`
  background-color: var(--clr-purple);
  color: white;
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: 500;
  text-transform: uppercase;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: var(--clr-sec);
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 6px;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
  }
`;

const Content = styled.div`
  flex: 1;
  padding: 2rem;
  overflow-y: auto;
  max-width: 1200px;
  margin: 0 auto;
  width: 100%;
`;

const StatsSection = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const StatCard = styled.div`
  background-color: rgba(255, 255, 255, 0.05);
  padding: 0.4rem 0.75rem;
  border-radius: 6px;
  display: flex;
  align-items: center;
  gap: 0.4rem;
`;

const StatNumber = styled.div`
  font-size: 1rem;
  font-weight: 700;
  color: var(--clr-purple);
`;

const StatLabel = styled.div`
  color: var(--clr-sec);
  font-size: 0.8rem;
  opacity: 0.7;
`;

const ReportsList = styled.div`
  background-color: var(--clr-prim);
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  overflow: hidden;
`;

const ReportsHeader = styled.div`
  padding: 1.5rem 2rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const SectionTitle = styled.h2`
  color: var(--clr-sec);
  margin: 0;
  font-size: 1.2rem;
`;

const RefreshButton = styled.button`
  background-color: var(--clr-purple);
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.9rem;
  transition: background-color 0.2s;
  
  &:hover:not(:disabled) {
    background-color: #b060d4;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ReportItem = styled.div`
  padding: 1.5rem 2rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: rgba(255, 255, 255, 0.02);
  }
  
  &:last-child {
    border-bottom: none;
  }
`;

const UserInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  flex: 1;
`;

const UserDisplay = styled.div`
  color: var(--clr-sec);
  font-size: 0.95rem;
  font-weight: 500;
`;

const UserId = styled.span`
  color: var(--clr-sec);
  font-family: monospace;
  font-size: 0.8rem;
  opacity: 0.7;
  margin-left: 0.5rem;
`;

const ReportCount = styled.div`
  color: var(--clr-sec);
  opacity: 0.7;
  font-size: 0.8rem;
`;

const RoomBreakdown = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
  margin-top: 0.25rem;
`;

const RoomBadgeSmall = styled.span`
  background-color: var(--clr-purple);
  color: white;
  padding: 0.15rem 0.4rem;
  border-radius: 8px;
  font-size: 0.7rem;
  font-weight: 500;
  opacity: 0.8;
`;

const LastReportTime = styled.div`
  color: var(--clr-sec);
  opacity: 0.6;
  font-size: 0.8rem;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 0.75rem;
  align-items: center;
`;

const ViewButton = styled.button`
  background-color: var(--clr-purple);
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.8rem;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: #b060d4;
  }
`;

const BanButton = styled.button`
  background-color: #ff4444;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.8rem;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: #dd3333;
  }
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: 3rem;
  color: var(--clr-sec);
  opacity: 0.7;
`;

const ErrorMessage = styled.div`
  text-align: center;
  padding: 3rem;
  color: #ff4444;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem;
  color: var(--clr-sec);
  opacity: 0.7;
`;

const UserSearchSection = styled.div`
  background-color: var(--clr-prim);
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 2rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
`;

const UserSearchResults = styled.div`
  margin-top: 1rem;
  max-height: 400px;
  overflow-y: auto;
`;

const UserSearchItem = styled.div`
  display: flex;
  align-items: center;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  background-color: var(--clr-bg);
  margin-bottom: 0.5rem;
  gap: 0.75rem;

  &:last-child {
    margin-bottom: 0;
  }
`;

const OnlineIndicator = styled.div`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: ${props => props.$isOnline ? '#4caf50' : '#666'};
  flex-shrink: 0;
`;

const UserSearchInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  flex: 1;
  min-width: 0;
`;

const UserSearchName = styled.div`
  color: var(--clr-sec);
  font-size: 0.95rem;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const UserSearchMeta = styled.div`
  color: var(--clr-sec);
  opacity: 0.6;
  font-size: 0.75rem;
`;

const BansSection = styled.div`
  background-color: var(--clr-prim);
  border-radius: 12px;
  padding: 1.5rem;
  margin-top: 2rem;
  margin-bottom: 2rem;
`;

const BansSectionTitle = styled.h2`
  color: var(--clr-sec);
  margin: 0 0 1rem 0;
  font-size: 1.1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const BansList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const BanItem = styled.div`
  background-color: var(--clr-bg);
  border-radius: 8px;
  padding: 1rem;
  border-left: 4px solid #e74c3c;
`;

const BanDetails = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
`;

const BanInfo = styled.div`
  flex: 1;
`;

const InternalNote = styled.div`
  color: var(--clr-sec);
  font-size: 0.9rem;
  margin-bottom: 0.25rem;
`;

const InternalNoteLabel = styled.span`
  color: var(--clr-sec);
  opacity: 0.7;
  font-weight: 600;
`;

const ExternalReason = styled.div`
  color: var(--clr-sec);
  font-size: 0.9rem;
  margin-bottom: 0.25rem;
`;

const ExternalReasonLabel = styled.span`
  color: var(--clr-sec);
  opacity: 0.7;
  font-weight: 600;
`;

const BannedMessage = styled.div`
  color: var(--clr-sec);
  font-size: 0.9rem;
  margin-bottom: 0.25rem;
  font-style: italic;
  background-color: rgba(255, 255, 255, 0.05);
  padding: 0.5rem;
  border-radius: 4px;
  margin-top: 0.5rem;
`;

const BannedMessageLabel = styled.span`
  color: var(--clr-sec);
  opacity: 0.7;
  font-weight: 600;
  font-style: normal;
`;

const BannedUser = styled.div`
  color: var(--clr-sec);
  font-size: 0.95rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
`;

const BannedUserLabel = styled.span`
  color: var(--clr-sec);
  opacity: 0.7;
  font-weight: 600;
`;

const BanId = styled.div`
  color: var(--clr-sec);
  font-size: 0.8rem;
  font-family: monospace;
  opacity: 0.6;
  margin-bottom: 0.5rem;
`;

const BanIdLabel = styled.span`
  color: var(--clr-sec);
  opacity: 0.7;
  font-weight: 600;
`;

const BanTiming = styled.div`
  text-align: right;
  font-size: 0.8rem;
`;

const BanTime = styled.div`
  color: var(--clr-sec);
  opacity: 0.7;
`;

const BanExpiry = styled.div`
  color: #e74c3c;
  font-weight: 500;
  margin-top: 0.25rem;
`;

const EmptyBans = styled.div`
  text-align: center;
  color: var(--clr-sec);
  opacity: 0.6;
  padding: 4rem 2rem;
  font-style: italic;
  min-height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const UnbanButton = styled.button`
  background-color: var(--clr-green);
  color: white;
  border: none;
  padding: 0.4rem 0.8rem;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.75rem;
  transition: background-color 0.2s;
  margin-top: 0.5rem;

  &:hover {
    background-color: #28a745;
  }

  &:disabled {
    background-color: #999;
    cursor: not-allowed;
  }
`;

const AdjustButton = styled.button`
  background-color: var(--clr-purple);
  color: white;
  border: none;
  padding: 0.4rem 0.8rem;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.75rem;
  transition: background-color 0.2s;
  margin-top: 0.5rem;
  margin-left: 0.5rem;

  &:hover:not(:disabled) {
    background-color: #b060d4;
  }

  &:disabled {
    background-color: #999;
    cursor: not-allowed;
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 3000;
`;

const ModalContent = styled.div`
  background-color: var(--clr-prim);
  border-radius: 12px;
  padding: 2rem;
  max-width: 500px;
  width: 90%;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
`;

const ModalTitle = styled.h3`
  color: var(--clr-sec);
  margin: 0 0 1.5rem 0;
  font-size: 1.3rem;
`;

const ModalLabel = styled.label`
  display: block;
  color: var(--clr-sec);
  font-size: 0.9rem;
  margin-bottom: 0.5rem;
  font-weight: 500;
`;

const ModalInput = styled.input`
  width: 100%;
  background-color: var(--clr-bg);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 6px;
  padding: 0.75rem;
  color: var(--clr-sec);
  font-size: 0.95rem;
  margin-bottom: 1.5rem;

  &:focus {
    outline: none;
    border-color: var(--clr-purple);
  }
`;

const ModalButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
`;

const ModalButton = styled.button`
  padding: 0.6rem 1.2rem;
  border-radius: 6px;
  border: none;
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: 500;
  transition: background-color 0.2s;

  &.primary {
    background-color: var(--clr-purple);
    color: white;

    &:hover:not(:disabled) {
      background-color: #b060d4;
    }

    &:disabled {
      background-color: #999;
      cursor: not-allowed;
    }
  }

  &.secondary {
    background-color: rgba(255, 255, 255, 0.1);
    color: var(--clr-sec);

    &:hover {
      background-color: rgba(255, 255, 255, 0.15);
    }
  }
`;

const SearchForm = styled.form`
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1rem;
`;

const SearchInput = styled.input`
  flex: 1;
  background-color: var(--clr-bg);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 6px;
  padding: 0.5rem 1rem;
  color: var(--clr-sec);
  font-size: 0.9rem;

  &:focus {
    outline: none;
    border-color: var(--clr-purple);
  }

  &::placeholder {
    color: var(--clr-sec);
    opacity: 0.5;
  }
`;

const ClearButton = styled.button`
  background-color: rgba(255, 255, 255, 0.1);
  color: var(--clr-sec);
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.9rem;
  transition: background-color 0.2s;

  &:hover {
    background-color: rgba(255, 255, 255, 0.15);
  }
`;

const PaginationControls = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1rem;
  margin-top: 1rem;
  padding: 1rem;
`;

const PaginationButton = styled.button`
  background-color: var(--clr-purple);
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.85rem;
  transition: background-color 0.2s;

  &:hover:not(:disabled) {
    background-color: #b060d4;
  }

  &:disabled {
    background-color: rgba(255, 255, 255, 0.1);
    color: var(--clr-sec);
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PaginationInfo = styled.div`
  color: var(--clr-sec);
  font-size: 0.9rem;
`;

const SkeletonBanItem = styled.div`
  background-color: var(--clr-bg);
  border-radius: 8px;
  padding: 1rem;
  border-left: 4px solid rgba(231, 76, 60, 0.3);
  animation: pulse 1.5s ease-in-out infinite;

  @keyframes pulse {
    0%, 100% {
      opacity: 1;
    }
    50% {
      opacity: 0.5;
    }
  }
`;

const SkeletonLine = styled.div`
  background-color: rgba(255, 255, 255, 0.1);
  height: ${props => props.height || '1rem'};
  width: ${props => props.width || '100%'};
  border-radius: 4px;
  margin-bottom: ${props => props.marginBottom || '0.5rem'};
`;

const ModReportsDashboard = ({ isOpen, onClose, room = 'general', isGlobalMod = false }) => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [reportDetailsOpen, setReportDetailsOpen] = useState(false);
  const [banModalOpen, setBanModalOpen] = useState(false);
  const [userToBan, setUserToBan] = useState(null);
  const [recentBans, setRecentBans] = useState([]);
  const [bansLoading, setBansLoading] = useState(false);
  const [unbanningId, setUnbanningId] = useState(null);
  const [bansPage, setBansPage] = useState(1);
  const [bansPagination, setBansPagination] = useState(null);
  const [bansSearch, setBansSearch] = useState('');
  const [adjustModalOpen, setAdjustModalOpen] = useState(false);
  const [banToAdjust, setBanToAdjust] = useState(null);
  const [newUnbanDate, setNewUnbanDate] = useState('');
  const [adjusting, setAdjusting] = useState(false);
  const [userSearch, setUserSearch] = useState('');
  const [userSearchResults, setUserSearchResults] = useState([]);
  const [userSearchLoading, setUserSearchLoading] = useState(false);

  const fetchReports = async () => {
    setLoading(true);
    setError('');
    
    try {
      const token = await getAuth().currentUser.getIdToken();
      
      // Use global endpoint for global mods, room-specific endpoint otherwise
      const endpoint = room === 'global' ? 
        `${API_URL}report/mod/global` : 
        `${API_URL}report/mod/${room}`;
      
      const response = await fetch(endpoint, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setReports(data.reports || []);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to fetch reports');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchRecentBans = async (page = 1, search = '') => {
    setBansLoading(true);

    try {
      const token = await getAuth().currentUser.getIdToken();
      const roomParam = room !== 'global' ? `room=${room}` : '';
      const pageParam = `page=${page}`;
      const limitParam = `limit=10`;
      const searchParam = search ? `search=${encodeURIComponent(search)}` : '';

      const params = [roomParam, pageParam, limitParam, searchParam].filter(p => p).join('&');
      const url = `${API_URL}mod/recent-ip-bans${params ? `?${params}` : ''}`;

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setRecentBans(data.bans || []);
        setBansPagination(data.pagination || null);
      } else {
        console.error('Failed to fetch recent bans');
      }
    } catch (err) {
      console.error('Error fetching recent bans:', err);
    } finally {
      setBansLoading(false);
    }
  };

  const handleUnban = async (banId) => {
    setUnbanningId(banId);

    try {
      const token = await getAuth().currentUser.getIdToken();
      const roomParam = room !== 'global' ? `?room=${room}` : '';

      const response = await fetch(`${API_URL}mod/unban-ip/${banId}${roomParam}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
      });

      if (response.ok) {
        toast.success('User unbanned successfully');
        // Refresh the bans list
        fetchRecentBans(bansPage, bansSearch);
      } else {
        const errorData = await response.json();
        toast.error(`Failed to unban: ${errorData.error || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('Error unbanning:', err);
      toast.error('Network error while unbanning user');
    } finally {
      setUnbanningId(null);
    }
  };

  const handleOpenAdjustModal = (ban) => {
    setBanToAdjust(ban);
    // Set default to current unban date formatted for datetime-local input
    const currentDate = new Date(ban.unbanDate);
    const formatted = currentDate.toISOString().slice(0, 16);
    setNewUnbanDate(formatted);
    setAdjustModalOpen(true);
  };

  const handleCloseAdjustModal = () => {
    setAdjustModalOpen(false);
    setBanToAdjust(null);
    setNewUnbanDate('');
  };

  const handleAdjustBan = async () => {
    if (!banToAdjust || !newUnbanDate) return;

    setAdjusting(true);

    try {
      const token = await getAuth().currentUser.getIdToken();
      const roomParam = room !== 'global' ? `?room=${room}` : '';

      const response = await fetch(`${API_URL}mod/adjust-ban-date/${banToAdjust.id}${roomParam}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          newUnbanDate: new Date(newUnbanDate).toISOString(),
        }),
      });

      if (response.ok) {
        toast.success('Ban expiry date adjusted successfully');
        handleCloseAdjustModal();
        // Refresh the bans list
        fetchRecentBans(bansPage, bansSearch);
      } else {
        const errorData = await response.json();
        toast.error(`Failed to adjust ban: ${errorData.error || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('Error adjusting ban:', err);
      toast.error('Network error while adjusting ban');
    } finally {
      setAdjusting(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchReports();
      fetchRecentBans(bansPage, bansSearch);
    }
  }, [isOpen, room]);

  useEffect(() => {
    if (isOpen) {
      fetchRecentBans(bansPage, bansSearch);
    }
  }, [bansPage, bansSearch]);

  // Debounced search effect
  useEffect(() => {
    if (!isOpen) return;

    const debounceTimer = setTimeout(() => {
      setBansPage(1); // Reset to first page on new search
    }, 500);

    return () => clearTimeout(debounceTimer);
  }, [bansSearch]);

  const searchUsers = async (query) => {
    if (query.length < 2) {
      setUserSearchResults([]);
      return;
    }
    setUserSearchLoading(true);
    try {
      const token = await getAuth().currentUser.getIdToken();
      const response = await fetch(`${API_URL}mod/search-users?q=${encodeURIComponent(query)}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setUserSearchResults(data.users || []);
      }
    } catch (err) {
      console.error('Error searching users:', err);
    } finally {
      setUserSearchLoading(false);
    }
  };

  // Debounced user search effect
  useEffect(() => {
    if (!isOpen) return;

    const timer = setTimeout(() => {
      if (userSearch) {
        searchUsers(userSearch);
      } else {
        setUserSearchResults([]);
      }
    }, 400);

    return () => clearTimeout(timer);
  }, [userSearch, isOpen]);

  const handleViewReports = (userId) => {
    setSelectedUserId(userId);
    setReportDetailsOpen(true);
  };

  const handleBanUser = (userId, reportCount, nickname = null) => {
    // Create a mock message object for the ban modal
    setUserToBan({
      userId: userId,
      messageText: nickname ? `User: ${nickname}` : `User with ${reportCount} report(s)`,
      nickname: nickname || 'Reported User'
    });
    setBanModalOpen(true);
  };

  const handleBanComplete = () => {
    setBanModalOpen(false);
    setUserToBan(null);
    // Refresh reports after successful ban
    fetchReports();
  };

  const handleReportDetailsClose = () => {
    setReportDetailsOpen(false);
    setSelectedUserId(null);
    // Refresh reports in case any were resolved
    fetchReports();
  };

  if (!isOpen) return null;

  const totalReports = reports.reduce((sum, report) => sum + report.reportCount, 0);
  const totalUsers = reports.length;

  return (
    <FullScreenOverlay>
      <Header>
        <Title>
          Reports Dashboard
          <RoomBadge>{room === 'global' ? 'All Default Rooms' : room.toUpperCase()}</RoomBadge>
        </Title>
        <CloseButton onClick={onClose}>×</CloseButton>
      </Header>

      <Content>
        <UserSearchSection>
          <SectionTitle style={{ marginBottom: '1rem' }}>Search Users</SectionTitle>
          <SearchForm onSubmit={(e) => e.preventDefault()}>
            <SearchInput
              type="text"
              placeholder="Search by nickname (min 2 chars)..."
              value={userSearch}
              onChange={(e) => setUserSearch(e.target.value)}
            />
            {userSearch && (
              <ClearButton
                type="button"
                onClick={() => {
                  setUserSearch('');
                  setUserSearchResults([]);
                }}
              >
                Clear
              </ClearButton>
            )}
          </SearchForm>
          {userSearchLoading && <LoadingMessage>Searching...</LoadingMessage>}
          {userSearchResults.length > 0 && (
            <UserSearchResults>
              {userSearchResults.map(user => (
                <UserSearchItem key={user.id}>
                  <OnlineIndicator $isOnline={user.isOnline} />
                  <UserSearchInfo>
                    <UserSearchName>
                      {user.nickname}
                      <UserId>({user.id.substring(0, 8)}...)</UserId>
                      {user.isOnline && <RoomBadgeSmall>{user.currentRoom}</RoomBadgeSmall>}
                    </UserSearchName>
                    <UserSearchMeta>
                      {user.isOnline ? 'Online' : `Last active ${dayjs(user.lastActiveAt).fromNow()}`}
                    </UserSearchMeta>
                  </UserSearchInfo>
                  <BanButton onClick={() => handleBanUser(user.id, 0, user.nickname)}>
                    Ban User
                  </BanButton>
                </UserSearchItem>
              ))}
            </UserSearchResults>
          )}
          {userSearch.length >= 2 && !userSearchLoading && userSearchResults.length === 0 && (
            <EmptyState>No users found matching "{userSearch}"</EmptyState>
          )}
        </UserSearchSection>

        <ReportsList>
          <ReportsHeader>
            <SectionTitle>Reported Users</SectionTitle>
            <StatsSection>
              <StatCard>
                <StatNumber>{totalReports}</StatNumber>
                <StatLabel>Reports</StatLabel>
              </StatCard>
              <StatCard>
                <StatNumber>{totalUsers}</StatNumber>
                <StatLabel>Users</StatLabel>
              </StatCard>
              <StatCard>
                <StatNumber>{reports.filter(r => r.reportCount >= 3).length}</StatNumber>
                <StatLabel>High Priority</StatLabel>
              </StatCard>
              <RefreshButton onClick={fetchReports} disabled={loading}>
                {loading ? 'Loading...' : 'Refresh'}
              </RefreshButton>
            </StatsSection>
          </ReportsHeader>

          {loading && reports.length === 0 ? (
            <LoadingMessage>Loading reports...</LoadingMessage>
          ) : error ? (
            <ErrorMessage>{error}</ErrorMessage>
          ) : reports.length === 0 ? (
            <EmptyState>
              No pending reports found. Great job keeping the community clean! 🎉
            </EmptyState>
          ) : (
            reports.map((report) => (
              <ReportItem key={report.userId}>
                <UserInfo>
                  <UserDisplay>
                    {report.nickname || 'Anonymous'}
                    <UserId>({report.userId.substring(0, 8)}...)</UserId>
                  </UserDisplay>
                  <ReportCount>
                    {report.reportCount} report{report.reportCount !== 1 ? 's' : ''}
                  </ReportCount>
                  {report.reporterId && (
                    <ReportCount>
                      Reported by: {report.reporterNickname || `${report.reporterId.substring(0, 8)}...`}
                    </ReportCount>
                  )}
                  {room === 'global' && report.roomBreakdown && (
                    <RoomBreakdown>
                      {Object.entries(report.roomBreakdown).map(([roomId, count]) => (
                        <RoomBadgeSmall key={roomId}>
                          {roomId}: {count}
                        </RoomBadgeSmall>
                      ))}
                    </RoomBreakdown>
                  )}
                  <LastReportTime>
                    Latest: {dayjs(report.latestReport).fromNow()}
                  </LastReportTime>
                </UserInfo>
                <ActionButtons>
                  <ViewButton onClick={() => handleViewReports(report.userId)}>
                    View Details
                  </ViewButton>
                  <BanButton onClick={() => handleBanUser(report.userId, report.reportCount)}>
                    Ban User
                  </BanButton>
                </ActionButtons>
              </ReportItem>
            ))
          )}
        </ReportsList>

        <BansSection>
          <BansSectionTitle>
            🚫 Recent Bans
          </BansSectionTitle>
          <SearchForm onSubmit={(e) => e.preventDefault()}>
            <SearchInput
              type="text"
              placeholder="Search by ban ID, nickname, reason..."
              value={bansSearch}
              onChange={(e) => setBansSearch(e.target.value)}
            />
            {bansSearch && (
              <ClearButton
                type="button"
                onClick={() => {
                  setBansSearch('');
                  setBansPage(1);
                }}
              >
                Clear
              </ClearButton>
            )}
          </SearchForm>
          {bansLoading && recentBans.length === 0 ? (
            <BansList>
              {[...Array(3)].map((_, index) => (
                <SkeletonBanItem key={index}>
                  <SkeletonLine width="40%" height="1.2rem" marginBottom="0.75rem" />
                  <SkeletonLine width="60%" height="0.9rem" marginBottom="0.5rem" />
                  <SkeletonLine width="80%" height="0.9rem" marginBottom="0.5rem" />
                  <SkeletonLine width="70%" height="0.9rem" marginBottom="0" />
                </SkeletonBanItem>
              ))}
            </BansList>
          ) : recentBans.length > 0 ? (
            <>
              <BansList style={{ opacity: bansLoading ? 0.5 : 1, transition: 'opacity 0.2s' }}>
              {recentBans.map((ban, index) => (
                <BanItem key={index}>
                  <BanDetails>
                    <BanInfo>
                      <BanId>
                        <BanIdLabel>Ban ID: </BanIdLabel>
                        {ban.id}
                      </BanId>
                      {(ban.bannedUser || ban.bannedUserNickname) && (
                        <BannedUser>
                          <BannedUserLabel>Banned user: </BannedUserLabel>
                          {ban.bannedUserNickname || ban.bannedUser?.nickname || ban.bannedUser?.accountNumber || 'Unknown'}
                        </BannedUser>
                      )}
                      <BanExpiry style={{ marginBottom: '0.5rem', marginTop: 0 }}>
                        Expires: {dayjs(ban.unbanDate).format('MMM D, YYYY h:mm A')}
                      </BanExpiry>
                      <InternalNote>
                        <InternalNoteLabel>Internal Note: </InternalNoteLabel>
                        {ban.internalNote}
                      </InternalNote>
                      <ExternalReason>
                        <ExternalReasonLabel>Reason shown to banned user: </ExternalReasonLabel>
                        {ban.externalReason}
                      </ExternalReason>
                      {ban.bannedMessage && (
                        <BannedMessage>
                          <BannedMessageLabel>Message: </BannedMessageLabel>
                          "{ban.bannedMessage}"
                        </BannedMessage>
                      )}
                    </BanInfo>
                    <BanTiming>
                      <BanTime>{dayjs(ban.createdAt).format('MMM D, h:mm A')}</BanTime>
                      <UnbanButton
                        onClick={() => handleUnban(ban.id)}
                        disabled={unbanningId === ban.id}
                      >
                        {unbanningId === ban.id ? 'Unbanning...' : 'Unban'}
                      </UnbanButton>
                      <AdjustButton
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenAdjustModal(ban);
                        }}
                        disabled={unbanningId === ban.id}
                      >
                        Adjust Date
                      </AdjustButton>
                    </BanTiming>
                  </BanDetails>
                </BanItem>
              ))}
            </BansList>
            {bansPagination && bansPagination.totalPages > 1 && (
              <PaginationControls>
                <PaginationButton
                  onClick={() => setBansPage(prev => Math.max(1, prev - 1))}
                  disabled={bansPage === 1}
                >
                  Previous
                </PaginationButton>
                <PaginationInfo>
                  Page {bansPagination.page} of {bansPagination.totalPages} ({bansPagination.total} total)
                </PaginationInfo>
                <PaginationButton
                  onClick={() => setBansPage(prev => Math.min(bansPagination.totalPages, prev + 1))}
                  disabled={bansPage === bansPagination.totalPages}
                >
                  Next
                </PaginationButton>
              </PaginationControls>
            )}
          </>
          ) : (
            <EmptyBans>{bansSearch ? 'No bans found matching search' : 'No recent IP bans'}</EmptyBans>
          )}
        </BansSection>
      </Content>

      <ReportDetailsModal
        isOpen={reportDetailsOpen}
        onClose={handleReportDetailsClose}
        userId={selectedUserId}
        room={room}
      />

      <ModBanModal
        isOpen={banModalOpen}
        onClose={() => setBanModalOpen(false)}
        message={userToBan}
        room={room}
        onBanComplete={handleBanComplete}
      />

      {adjustModalOpen && (
        <ModalOverlay onClick={handleCloseAdjustModal}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalTitle>Adjust Ban Expiry Date</ModalTitle>
            {banToAdjust && (
              <>
                <div style={{ marginBottom: '1rem', color: 'var(--clr-sec)', fontSize: '0.9rem' }}>
                  <strong>Ban ID:</strong> {banToAdjust.id}<br />
                  <strong>User:</strong> {banToAdjust.bannedUserNickname || 'Unknown'}<br />
                  <strong>Current Expiry:</strong> {dayjs(banToAdjust.unbanDate).format('MMM D, YYYY h:mm A')}
                </div>
                <ModalLabel>New Expiry Date & Time</ModalLabel>
                <ModalInput
                  type="datetime-local"
                  value={newUnbanDate}
                  onChange={(e) => setNewUnbanDate(e.target.value)}
                  min={new Date().toISOString().slice(0, 16)}
                />
                <ModalButtonGroup>
                  <ModalButton
                    className="secondary"
                    onClick={handleCloseAdjustModal}
                    disabled={adjusting}
                  >
                    Cancel
                  </ModalButton>
                  <ModalButton
                    className="primary"
                    onClick={handleAdjustBan}
                    disabled={adjusting || !newUnbanDate}
                  >
                    {adjusting ? 'Adjusting...' : 'Adjust Ban Date'}
                  </ModalButton>
                </ModalButtonGroup>
              </>
            )}
          </ModalContent>
        </ModalOverlay>
      )}
    </FullScreenOverlay>
  );
};

export default ModReportsDashboard;